import { useApiClient } from '../lib/api-client.js';
import { useQueryClient, useQuery, useMutation } from '@tanstack/react-query';
import { useMemo, useCallback } from 'react';

/**
 * Transform a list API record to the SavedListView shape used by the UI.
 */
function toSavedListView(record) {
    const columns = (record.columns || [])
        .sort((a, b) => a.position - b.position)
        .map((c) => c.fieldKey);
    const filters = (record.filters || []).reduce((acc, f) => {
        acc[f.fieldKey] = f.value;
        return acc;
    }, {});
    // Derive scope from owners: if any owner has userId set, it's personal
    const owners = record.owners || [];
    const scope = owners.some((o) => o.userId)
        ? 'personal'
        : 'shared';
    return {
        id: record.id,
        name: record.name,
        scope,
        columns,
        filters,
        sortField: record.sortField || undefined,
        sortOrder: record.sortOrder || undefined,
        createdBy: record.createdBy || undefined,
        createdAt: record.createdAt,
    };
}
/**
 * Convert a SavedListView back to the API create/update body shape.
 */
function toApiBody(view) {
    return {
        name: view.name,
        scope: view.scope,
        columns: view.columns.map((fieldKey, index) => ({
            fieldKey,
            position: index,
        })),
        filters: Object.entries(view.filters || {})
            .filter(([, value]) => value !== undefined)
            .map(([fieldKey, value]) => ({
            fieldKey,
            value,
        })),
        sortField: view.sortField,
        sortOrder: view.sortOrder,
    };
}
/**
 * Hook to manage saved list views via the Lists API.
 * Fetches all lists visible to the current user for a given entity.
 */
function useSavedListViews(entityName) {
    const apiClient = useApiClient();
    const queryClient = useQueryClient();
    const { data: listsResponse, isLoading } = useQuery({
        queryKey: ['saved-list-views', entityName],
        queryFn: async () => {
            return await apiClient.get(`/lists/entity/${entityName}`);
        },
        staleTime: 2 * 60 * 1000,
    });
    const views = useMemo(() => {
        const records = listsResponse?.data;
        if (!Array.isArray(records))
            return [];
        return records.map(toSavedListView);
    }, [listsResponse]);
    const invalidate = useCallback(() => {
        queryClient.invalidateQueries({ queryKey: ['saved-list-views', entityName] });
    }, [queryClient, entityName]);
    const saveViewMutation = useMutation({
        mutationFn: async (view) => {
            await apiClient.post('/lists', {
                ...toApiBody(view),
                entityName,
            });
        },
        onSuccess: invalidate,
    });
    const updateViewMutation = useMutation({
        mutationFn: async (view) => {
            await apiClient.put(`/lists/${view.id}`, toApiBody(view));
        },
        onSuccess: invalidate,
    });
    const deleteViewMutation = useMutation({
        mutationFn: async ({ viewId }) => {
            await apiClient.delete(`/lists/${viewId}`);
        },
        onSuccess: invalidate,
    });
    return {
        views,
        isLoading,
        saveView: async (view) => {
            await saveViewMutation.mutateAsync(view);
        },
        updateView: async (view) => {
            await updateViewMutation.mutateAsync(view);
        },
        deleteView: async (viewId) => {
            await deleteViewMutation.mutateAsync({ viewId });
        },
    };
}

export { useSavedListViews };
//# sourceMappingURL=useSavedListViews.js.map
